import { ChangeEvent, useEffect } from 'react';
import { useParams } from 'react-router';
import { ErrorScreen, Stack } from 'commons/components';
import { useGetCurrency } from 'domains/Auth/hooks';
import { useCategories } from 'domains/Catalog/Categories/hooks';
import { useGetProvinces } from 'domains/Shipping/Addresses/hooks';
import { SelectedZonesInterface } from 'domains/Shipping/components/shared/ZoneManagement/DeliveryZoneCard/DeliveryZoneCard';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import Skeleton from './Skeleton';
import {
  useGetShippingRestrictions,
  useShippingMethodsOptions,
} from '../../../../hooks';
import ShippingDisponibilityCard from '../../../components/ShippingDisponibilityCard';
import ShippingMethodsScopeCard from '../../../components/ShippingMethodsScopeCard';
import ShippingRestrictionsCard from '../../../components/ShippingRestrictionsCard';
import useGetDetailActiveApp from '../../../EditCarriersPage/useGetDetailActiveApp';
import { ShippingRestrictionFormValues } from '../../types';

interface ShippingRestrictionFormProps {
  values: ShippingRestrictionFormValues;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onArrayChange: (name: string, value: unknown[]) => void;
  errors?: Partial<Record<keyof ShippingRestrictionFormValues, string>>;
  currentRestrictionId?: string;
}

function ShippingRestrictionForm({
  values,
  onChange,
  onArrayChange,
  errors = {},
  currentRestrictionId,
}: Readonly<ShippingRestrictionFormProps>): JSX.Element {
  const { id: carrierId } = useParams<{ id: string }>();
  const t = useTranslationShipping('deliveryMethods.shippingLimiting');
  const { symbol } = useGetCurrency();

  const {
    detailActiveApp,
    fetchDetailActiveApp,
    isLoading: isLoadingApp,
    isError: isErrorApp,
  } = useGetDetailActiveApp();

  const { methods, isLoading: isLoadingMethods } =
    useShippingMethodsOptions(carrierId);

  const {
    restrictions,
    isLoading: isLoadingRestrictions,
    isError: isErrorRestrictions,
    fetchRestrictions,
  } = useGetShippingRestrictions();

  const {
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
    fetchCategoryOptions,
  } = useCategories();

  const {
    fetchLocations,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
  } = useLocations();

  const {
    fetchProvinces,
    isLoading: isLoadingProvinces,
    isError: isErrorProvinces,
  } = useGetProvinces();

  useEffect(() => {
    fetchDetailActiveApp(carrierId);
    fetchRestrictions(carrierId);
    fetchCategoryOptions();
    fetchLocations();
    fetchProvinces();
  }, [
    fetchDetailActiveApp,
    fetchRestrictions,
    fetchCategoryOptions,
    fetchLocations,
    fetchProvinces,
    carrierId,
  ]);

  const isLoading =
    isLoadingApp ||
    isLoadingMethods ||
    isLoadingRestrictions ||
    isLoadingCategories ||
    isLoadingLocations ||
    isLoadingProvinces;
  const hasError =
    isErrorApp ||
    isErrorCategories ||
    isErrorLocations ||
    isErrorRestrictions ||
    isErrorProvinces;

  if (isLoading) {
    return <Skeleton />;
  }

  if (hasError) {
    return (
      <ErrorScreen
        description={t('genericError.text')}
        onRetry={() => {
          if (isErrorApp) fetchDetailActiveApp(carrierId);
          if (isErrorCategories) fetchCategoryOptions();
          if (isErrorLocations) fetchLocations();
          if (isErrorProvinces) fetchProvinces();
          if (isErrorRestrictions) fetchRestrictions(carrierId);
        }}
      />
    );
  }

  return (
    <Stack column align="stretch" spacing="base">
      <ShippingMethodsScopeCard
        appName={detailActiveApp?.name}
        methods={methods}
        restrictions={restrictions || []}
        currentRestrictionId={currentRestrictionId}
        value={values.shippingMethodsScope}
        onChange={onChange}
        selectedMethods={values.selectedShippingMethods || []}
        onMethodsChange={(methods) =>
          onArrayChange('selectedShippingMethods', methods)
        }
      />

      <ShippingDisponibilityCard
        distributionCenter={values.distributionCenterScope}
        deliveryZones={values.deliveryZonesScope}
        onChange={onChange}
        specificDistributionCenters={values.specificDistributionCenters}
        selectedZones={values.selectedZones}
        distanceInKm={values.deliveryDistanceInKm}
        onDistributionCentersChange={(centers) =>
          onArrayChange('specificDistributionCenters', centers)
        }
        onDeliveryZonesChange={(zones: SelectedZonesInterface[]) =>
          onArrayChange('selectedZones', zones)
        }
        errors={{
          distanceInKm: errors.deliveryDistanceInKm,
        }}
      />

      <ShippingRestrictionsCard
        restrictedProducts={values.productRestrictionType}
        cartWeight={values.weightRestrictionType}
        cartValue={values.valueRestrictionType}
        onChange={onChange}
        restrictedCategories={values.restrictedCategories}
        restrictedProductIds={values.restrictedProducts}
        onCategoriesChange={(categories) =>
          onArrayChange('restrictedCategories', categories)
        }
        onProductsChange={(products) =>
          onArrayChange('restrictedProducts', products)
        }
        cartWeightMin={values.cartWeightMin}
        cartWeightMax={values.cartWeightMax}
        cartValueMin={values.cartValueMin}
        cartValueMax={values.cartValueMax}
        errors={errors}
        currencySymbol={symbol}
      />
    </Stack>
  );
}

ShippingRestrictionForm.Skeleton = Skeleton;

export default ShippingRestrictionForm;
